import React, { useMemo } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";

type TooltipProps = {
  id?: string;
  text: string;
  children: React.ReactNode;
  isMobile?: boolean;
  color?: "white" | "blue";
  tooltipPlacement?: "top" | "bottom" | "left" | "right";
  tooltipTextAlign?: string;
  tooltipContainerClassName?: string;
  triggerClassName?: string;
  trackTooltipHover?: () => void;
  tooltipWidth?: number;
  tooltipMobileWidth?: number;
  tooltipMaxWidth?: number;
  cursor?: "pointer" | "help";
  positionStrategy?: "absolute" | "fixed";
  disableHover?: boolean;
  openEvents?: { click?: boolean, mouseover?: boolean };
  isOpen?: boolean;
};

//TODO: fix default styles for tooltip white
export const TooltipWrapper: React.FC<TooltipProps> = ({
  id,
  text,
  children,
  isMobile,
  color = "white",
  tooltipPlacement = "bottom",
  tooltipTextAlign = "text-start",
  trackTooltipHover,
  tooltipContainerClassName,
  triggerClassName = "",
  cursor = "pointer",
  positionStrategy = "absolute",
  disableHover = false,
  tooltipMobileWidth,
  tooltipWidth,
  tooltipMaxWidth,
  openEvents,
  isOpen
}) => {
  const uniqueId = useMemo(
    () => (id ? id : `tooltip-${Math.random().toString(36).slice(2, 9)}`),
    [id],
  );
  const openEventsDefault = { click: false, mouseover: true, ...openEvents };

  const backgroundColor =
    color === "blue" ? "!bg-surface-inverse-primary" : "!bg-white";
  const textColor = color === "blue" ? "!text-white" : "!text-text-primary";
  const width = isMobile ? (tooltipMobileWidth ?? tooltipWidth) : tooltipWidth;
  const widthClass =
    width !== undefined
      ? "!w-[var(--tooltip-w)]"
      : tooltipMaxWidth !== undefined
        ? "!w-max"
        : "!w-max !max-w-xs";
  const sizeStyle: React.CSSProperties | undefined =
    width !== undefined
      ? ({
          "--tooltip-w": `${width}px`,
          maxWidth: "none",
        } as React.CSSProperties)
      : tooltipMaxWidth !== undefined
        ? { maxWidth: `${tooltipMaxWidth}px` }
        : undefined;

  return (
    <>
      <div
        data-testid="ui-tooltip"
        className={`relative inline-flex items-center ${cursor === "help" ? "cursor-help" : "cursor-pointer"} ${triggerClassName}`}
        data-tooltip-id={uniqueId}
        data-tooltip-content={text}
        onMouseEnter={() => {
          if (!disableHover) {
            trackTooltipHover?.();
          }
        }}
      >
        {children}
      </div>

      <ReactTooltip
        id={uniqueId}
        place={tooltipPlacement}
        positionStrategy={positionStrategy}
        style={sizeStyle}
        className={`${backgroundColor} ${textColor} ${widthClass} z-10 !whitespace-normal !rounded-lg !px-3 !py-2 font-body !text-sm font-normal leading-normal !opacity-100 !transition-opacity ${tooltipTextAlign} ${tooltipContainerClassName ?? ""}`}
        opacity={1}
        delayShow={200}
        delayHide={0}
        globalCloseEvents={{ clickOutsideAnchor: true, escape: true, scroll: true }}
        openEvents={openEventsDefault}
        isOpen={isOpen}
      />
    </>
  );
};
